import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type {
  ApprovalRuleDto,
  GetApprovalRulesResponse,
  ApiError,
  Role,
} from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatAmount } from "@/lib/format";
import { validateApprovalRuleSet, type ValidatableRule } from "@/lib/approval-rules-validator";

// US-012 — Admin configures approval chain (levels + amount
// thresholds + approver role) and US-016 SLA fields (nudge /
// escalate hours per rule). Backend CRUD already shipped at
// /api/approval-rules (M3.A); this is the FE surface.
//
// MVP scope: list + add + delete. The validator banner re-runs the
// gap/overlap check (same logic the BE enforces) so the user sees
// the verdict before each Save. Edit-in-place + drag reorder are
// follow-ups — the BE PUT and PUT /reorder endpoints already exist.

export const Route = createFileRoute("/approval-rules")({
  head: () => ({
    meta: [
      { title: "Approval Rules — ProcureFlow" },
      {
        name: "description",
        content: "Configure multi-level approval chains and SLA timers.",
      },
    ],
  }),
  component: Page,
});

interface NewRule {
  level: number;
  approver_role: Role;
  amount_min: number;
  amount_max: number | null;
  nudge_after_hours: number | null;
  escalate_after_hours: number | null;
}

const ROLE_OPTIONS: Role[] = ["Requester", "Approver", "ProcurementOfficer", "Finance", "Admin"];

function Page() {
  const [rules, setRules] = useState<ApprovalRuleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      const res = await fetchWithAuth("/api/approval-rules");
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setLoadError(body?.message ?? "Failed to load approval rules.");
        return;
      }
      const body = (await res.json()) as GetApprovalRulesResponse;
      setRules(body.data);
    } catch {
      setLoadError("Failed to load approval rules.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleCreate(rule: NewRule) {
    setActionError(null);
    try {
      const res = await fetchWithAuth("/api/approval-rules", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rule),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setActionError(body?.message ?? "Failed to add rule.");
        return false;
      }
      await load();
      return true;
    } catch {
      setActionError("Unexpected error adding rule.");
      return false;
    }
  }

  async function handleDelete(rule: ApprovalRuleDto) {
    if (rules.length === 1) {
      setActionError("At least one rule must exist — cannot delete the last one.");
      return;
    }
    setPendingId(rule.id);
    setActionError(null);
    try {
      const res = await fetchWithAuth(`/api/approval-rules/${rule.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setActionError(body?.message ?? "Failed to delete rule.");
        return;
      }
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
    } catch {
      setActionError("Unexpected error deleting rule.");
    } finally {
      setPendingId(null);
    }
  }

  const validatableSet: ValidatableRule[] = rules.map((r) => ({
    amount_min: r.amount_min,
    amount_max: r.amount_max,
  }));
  const validation = validateApprovalRuleSet(validatableSet);

  return (
    <div className="space-y-6">
      <PageHeader
        description="Multi-level approval chains. Each rule routes PRs to an approver based on amount."
        action={
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-glow"
          >
            <Plus className="h-4 w-4" /> Add rule
          </button>
        }
      />

      <div
        className={
          validation.ok
            ? "rounded-lg border border-success/30 bg-success/5 px-4 py-2 text-xs [color:var(--success)]"
            : "rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive"
        }
      >
        {validation.ok ? "Rule set covers [0, ∞) with no gaps or overlaps." : validation.error}
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive"
        >
          {actionError}
        </div>
      )}

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        {loadError && (
          <div role="alert" className="border-b border-border px-5 py-3 text-sm text-destructive">
            {loadError}
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-5 py-3">Level</th>
              <th className="text-left font-medium px-3 py-3">Approver</th>
              <th className="text-right font-medium px-3 py-3">Min</th>
              <th className="text-right font-medium px-3 py-3">Max</th>
              <th className="text-right font-medium px-3 py-3">Nudge (h)</th>
              <th className="text-right font-medium px-3 py-3">Escalate (h)</th>
              <th className="text-right font-medium px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rules.length === 0 && !loadError && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-muted-foreground">
                  No approval rules configured. Add one to get started.
                </td>
              </tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-accent/40">
                <td className="px-5 py-3.5 font-medium">Level {r.level}</td>
                <td className="px-3 py-3.5">{r.approver_user?.name ?? r.approver_role ?? "—"}</td>
                <td className="px-3 py-3.5 text-right tabular-nums">
                  {formatAmount(r.amount_min)}
                </td>
                <td className="px-3 py-3.5 text-right tabular-nums">
                  {r.amount_max === null ? "∞" : formatAmount(r.amount_max)}
                </td>
                <td className="px-3 py-3.5 text-right tabular-nums">
                  {r.nudge_after_hours ?? "—"}
                </td>
                <td className="px-3 py-3.5 text-right tabular-nums">
                  {r.escalate_after_hours ?? "—"}
                </td>
                <td className="px-5 py-3.5 text-right">
                  <button
                    type="button"
                    onClick={() => void handleDelete(r)}
                    disabled={pendingId === r.id || rules.length === 1}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                    title={rules.length === 1 ? "At least one rule must exist." : "Delete rule"}
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddRuleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={async (rule) => {
          const ok = await handleCreate(rule);
          if (ok) setAddOpen(false);
        }}
      />
    </div>
  );
}

function AddRuleDialog({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (rule: NewRule) => Promise<void>;
}) {
  const [level, setLevel] = useState(1);
  const [approverRole, setApproverRole] = useState<Role>("Approver");
  const [minDollars, setMinDollars] = useState("0");
  const [maxDollars, setMaxDollars] = useState("");
  const [nudge, setNudge] = useState("");
  const [escalate, setEscalate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setLevel(1);
      setApproverRole("Approver");
      setMinDollars("0");
      setMaxDollars("");
      setNudge("");
      setEscalate("");
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  function toCents(dollars: string): number | null {
    const trimmed = dollars.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const minCents = toCents(minDollars);
    const maxCents = toCents(maxDollars); // null = unbounded
    if (minCents === null) {
      setSubmitting(false);
      return;
    }
    await onSubmit({
      level,
      approver_role: approverRole,
      amount_min: minCents,
      amount_max: maxCents,
      nudge_after_hours: nudge.trim() === "" ? null : Number(nudge),
      escalate_after_hours: escalate.trim() === "" ? null : Number(escalate),
    });
    setSubmitting(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-rule-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <h2 id="add-rule-title" className="text-lg font-semibold tracking-tight">
          Add approval rule
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="block text-xs font-medium">Level</span>
              <input
                type="number"
                min={1}
                value={level}
                onChange={(e) => setLevel(Number(e.target.value) || 1)}
                disabled={submitting}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Approver role</span>
              <select
                value={approverRole}
                onChange={(e) => setApproverRole(e.target.value as Role)}
                disabled={submitting}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Min amount</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={minDollars}
                onChange={(e) => setMinDollars(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Max amount ($, blank = ∞)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={maxDollars}
                onChange={(e) => setMaxDollars(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Nudge after (h)</span>
              <input
                type="number"
                min={0}
                value={nudge}
                onChange={(e) => setNudge(e.target.value)}
                disabled={submitting}
                placeholder="optional"
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Escalate after (h)</span>
              <input
                type="number"
                min={0}
                value={escalate}
                onChange={(e) => setEscalate(e.target.value)}
                disabled={submitting}
                placeholder="optional"
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5"
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Add rule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
